import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { DeviceSizeService } from '@frontend/sports/common/core/utils/media-query';
import { ThemeBorderRadius, ThemeElevated } from '@frontend/sports/common/ui/shared';
import { AccordionVariant, SportsThemedAccordionDirective } from '@frontend/sports/common/ui/sports-themed-accordion';
import { SportsThemedCardDirective } from '@frontend/sports/common/ui/sports-themed-card';
import { DialogAnimation, ModalDialogService } from '@frontend/sports/modal/feature';
import { Story } from '@frontend/sports/types/components/story-content';
import { StoryContentPayload, StoryContentShape } from '@frontend/sports/types/components/widget/types';
import { WidgetComponent } from '@frontend/sports/widget/core/feature';
import { DsAccordionModule } from '@frontend/ui/accordion';
import { DsButton } from '@frontend/ui/button';
import { DsCard } from '@frontend/ui/card';
import { HtmlNode } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Subscription } from 'rxjs';

import { StoryContentTrackingService } from './story-content-tracking.service';
import { ContentStory } from './story-content.model';
import { StoryContentService } from './story-content.service';
import { StoryPreviewComponent } from './story-preview.component';
import { StoryComponent } from './story-view/story.component';

@Component({
    selector: 'ms-story-content',
    templateUrl: './story-content.component.html',
    styleUrl: './story-content.component.scss',
    imports: [
        ScrollAdapterComponent,
        StoryPreviewComponent,
        DsCard,
        DsAccordionModule,
        IconCustomComponent,
        DsButton,
        SportsThemedAccordionDirective,
        SportsThemedCardDirective,
    ],
    providers: [StoryContentTrackingService],
    host: {
        'class': 'story-content-container',
        '[class.square]': 'square',
        '[class.circle]': 'circle',
        '[class.single]': 'strech',
    },
})
export class StoryContentComponent extends WidgetComponent<StoryContentPayload> {
    square = false;
    circle = false;

    get strech(): boolean {
        return this.stories?.length === 1;
    }

    dismissable: boolean;
    stories: ContentStory[];
    storyWithImage?: string;
    title?: string;
    viewTitle?: string;
    expanded: boolean;
    readonly themeBorderRadius: ThemeBorderRadius = {
        'sportingbet-br': { hasRadius: true },
        'betboo-br': { hasRadius: true },
    };
    readonly themeCardElevated: ThemeElevated = { 'sportingbet-br': { isElevated: false }, 'betboo-br': { isElevated: false } };

    private contentSubscription?: Subscription;

    public sitecore = inject(Sitecore);
    private html = inject(HtmlNode);
    private modal = inject(ModalDialogService);
    private storyTracking = inject(StoryContentTrackingService);
    private storyService = inject(StoryContentService);
    private epcotConfig = inject(EpcotConfig);
    private deviceSizeService = inject(DeviceSizeService);

    isEpcotEnabled = signal(this.epcotConfig.isEnabled);
    themedAccordionVariant: AccordionVariant = {
        'mgm-dice': 'surface-low',
        'betboo-br': 'surface-low',
        'sportingbet-br': 'surface-low',
        'sports-interaction': 'surface-highest',
    };
    isMobile = signal(this.deviceSizeService.isMobile());

    toggle(): void {
        this.expanded = !this.expanded;
    }

    protected override onData(data: StoryContentPayload): void {
        this.setStories(data.stories);

        this.dismissable = data.dismissable;
        this.title = data.title;
        this.viewTitle = data.viewTitle;

        this.square = data.shape === StoryContentShape.Square;
        this.circle = data.shape === StoryContentShape.Circle;

        this.expanded = !this.storyService.isViewed(data);
        this.hasData = !(this.storyService.isDismissed(this.config.id) || this.stories.length === 0);
        this.setWidgetLoaded();
    }

    dismiss(event: Event): void {
        event.stopPropagation();

        this.storyService.setDismissed(this.config.id);
        this.storyTracking.trackStoryDismissModule();

        this.hidden = true;

        this.contentSubscription?.unsubscribe();
    }

    openStory(story: ContentStory): void {
        const storyIndex = this.stories.indexOf(story);
        this.storyTracking.trackStoryClick(story.title, storyIndex);

        this.html.setCssClass('popup-on', true);
        this.modal.openPopup(StoryComponent, {
            data: {
                stories: this.stories,
                activeStory: storyIndex,
            },
            settings: {
                cssClass: 'modal-popup story-content-modal',
                fit: false,
                showPageHeader: false,
                showDialogHeader: false,
                isModalDialog: true,
                animation: DialogAnimation.SlideInFromBottom,
                closeAnimation: DialogAnimation.SlideOutFromTop,
                afterResulted: () => {
                    this.html.setCssClass('popup-on', false);
                },
            },
        });
    }

    private setStories(stories: Story[]): void {
        this.contentSubscription?.unsubscribe();

        this.mapStories(stories);

        const storyWithImage = stories.find((s) => s.tileImage);
        this.storyWithImage = storyWithImage?.id;

        this.contentSubscription = this.storyService.contentChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.mapStories(stories));
    }

    private mapStories(stories: Story[]): void {
        this.stories = this.storyService.map(stories);
        this.hasData = this.stories.length > 0;
    }
}
